import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import { STATUS_COLOR, STATUS_BORDER_COLOR, leftColumns } from '../constant';
import {
  FilterDropdown,
  TableSortDisplayArrow,
  SpinnerLoading,
} from '../../../Common';

import './HolidayAnalysisCalendarYearly.scss';

export default function HolidayAnalysisCalendarYearly({
  holidayData = null,
  selectedYear,
  pageRequest,
  setColumnName,
  columnData,
  applyFilter,
  columnName,
  setPageRequest,
  handleManualSort,
  checkColumnIfSorted,
  isLoadingData,
  isLoadingColumn,
  handleAutoSort,
}) {
  const [year, setYear] = useState(moment().year());
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    setYear(selectedYear);
  }, [selectedYear]);

  // Generate map of leave records
  const rows = useMemo(() => {
    const leaveRecords = holidayData?.dataset?.[0] ?? [];
    const map = {};
    leaveRecords.forEach(rec => {
      const id = rec.EmpCd.trim();
      if (!map[id]) {
        map[id] = {
          department: rec.Dept.trim(),
          section: rec.Section.trim(),
          employee: rec.Name.trim(),
          leaves: [],
        };
      }
      let start = moment(rec.LvApprFromDt);
      let end = moment(rec.LvApprToDt);
      if (end.isBefore(start, 'day')) [start, end] = [end, start];
      map[id].leaves.push({ start, end, type: rec.LeaveDesc?.trim() });
    });
    return Object.values(map);
  }, [holidayData]);

  // Generate weeks (52 weeks fixed, with the last week stretching to Dec 31)
  const weeks = useMemo(() => {
    const w = [];
    let cursor = moment(`${year}-01-01`);
    const eoy = moment(`${year}-12-31`);
    for (let i = 1; i <= 52; i += 1) {
      const weekStart = cursor.clone();
      // if this is week 52, stretch all the way to Dec 31
      const weekEnd =
        i === 52 ? eoy : moment.min(weekStart.clone().add(6, 'days'), eoy);
      w.push({ weekNumber: i, weekStart, weekEnd });
      cursor = weekEnd.clone().add(1, 'day');
    }
    return w;
  }, [year]);

  // Generate month headers (Jan, Feb, Mar etc)
  const monthHeaders = useMemo(() => {
    const hdrs = [];
    let currentMonth = weeks[0].weekStart.month();
    let span = 0;
    weeks.forEach(w => {
      const m = w.weekEnd.month();
      if (m !== currentMonth) {
        hdrs.push({
          month: moment().month(currentMonth).format('MMM'),
          span,
        });
        currentMonth = m;
        span = 1;
      } else {
        span += 1;
      }
    });
    hdrs.push({
      month: moment().month(currentMonth).format('MMM'),
      span,
    });
    return hdrs;
  }, [weeks]);

  // Group rows by department
  const grouped = useMemo(
    () =>
      rows.reduce((acc, row) => {
        (acc[row.department] ||= []).push(row);
        return acc;
      }, {}),
    [rows],
  );

  // Toggle collapse state for each department
  const toggle = dept => setCollapsed(p => ({ ...p, [dept]: !p[dept] }));

  const allDepartments = Object.keys(grouped);
  const allCollapsed =
    allDepartments.length > 0 && allDepartments.every(dept => collapsed[dept]);

  // Loading state
  if (isLoadingData) {
    return <SpinnerLoading spinnerSize="2rem" height="60vh" />;
  }

  return (
    <div className="holiday-calendar-container yearly">
      <div className="table-scroll">
        <table className="holiday-calendar-table">
          <colgroup>
            {leftColumns.map((col, i) => (
              <col
                className="first-row-left-header"
                key={i}
                style={{
                  width: `${col?.width}`,
                }}
              />
            ))}
          </colgroup>
          <thead>
            {/* year nav */}
            <tr>
              <th colSpan={leftColumns.length} className="left-header">
                <div className="d-flex align-items-center gap-2 px-3">
                  <button
                    className="all-collapse-toggle p-0"
                    type="button"
                    onClick={() => {
                      if (allCollapsed) {
                        setCollapsed({});
                      } else {
                        const newCollapsed = {};
                        allDepartments.forEach(dept => {
                          newCollapsed[dept] = true;
                        });
                        setCollapsed(newCollapsed);
                      }
                    }}
                  >
                    {allCollapsed ? '[+]' : '[-]'}
                  </button>
                </div>
              </th>
              {monthHeaders.map((h, i) => (
                <th key={i} colSpan={h.span} className="month-header">
                  {h.month}
                </th>
              ))}
            </tr>
            {/* week numbers */}
            <tr>
              {leftColumns.map(col => (
                <th key={col.accessor} className="left-header">
                  <div className="d-flex flex-row align-items-center justify-content-center">
                    <div
                      className="cursor-pointer"
                      onClick={() => handleAutoSort(col?.key)}
                    >
                      {col.header}
                    </div>
                    <TableSortDisplayArrow
                      direction={checkColumnIfSorted(col?.key)}
                    />
                    <FilterDropdown
                      handleSetColumnName={() => {
                        setColumnName(col?.key);
                      }}
                      setColumnName={setColumnName}
                      columnData={columnData}
                      applyFilter={applyFilter}
                      columnName={columnName}
                      pageRequest={pageRequest}
                      setPageRequest={setPageRequest}
                      handleManualSort={handleManualSort}
                      isLoading={isLoadingColumn}
                    />
                  </div>
                </th>
              ))}
              {weeks.map(w => (
                <th
                  key={w.weekNumber}
                  className="date-header text-center"
                  title={`${w.weekStart.format('MMM D')} – ${w.weekEnd.format(
                    'MMM D',
                  )}`}
                >
                  {w.weekNumber}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([dept, deptRows]) => (
              <React.Fragment key={dept}>
                <tr className="department-row" onClick={() => toggle(dept)}>
                  <td colSpan={leftColumns.length + weeks.length}>
                    <div className="d-flex justify-content-start align-items-center px-3">
                      <span className="collapse-toggle">
                        [{collapsed[dept] ? '+' : '-'}]
                      </span>{' '}
                      <strong>{dept}</strong>
                    </div>
                  </td>
                </tr>
                {!collapsed[dept] &&
                  deptRows.map((row, rIdx) => (
                    <tr key={rIdx}>
                      {leftColumns.map(col => (
                        <td className="left-data" key={col.accessor}>
                          {!col?.isBlank && row[col.accessor]}
                        </td>
                      ))}
                      {weeks.map((w, wi) => {
                        const hits = row.leaves
                          .filter(l => {
                            const s = l.start;
                            const e = l.end;
                            return (
                              s.isBetween(
                                w.weekStart,
                                w.weekEnd,
                                'day',
                                '[]',
                              ) ||
                              e.isBetween(
                                w.weekStart,
                                w.weekEnd,
                                'day',
                                '[]',
                              ) ||
                              (s.isSameOrBefore(w.weekStart, 'day') &&
                                e.isSameOrAfter(w.weekEnd, 'day'))
                            );
                          })
                          .sort(
                            (a, b) =>
                              a.start.diff(b.start) || a.end.diff(b.end),
                          );

                        let bg = '';
                        let borderColor = '';
                        if (hits.length) {
                          const leaveType = hits[hits.length - 1].type;
                          if (
                            leaveType
                              .toLowerCase()
                              .includes('annual leave planning')
                          ) {
                            bg = STATUS_COLOR.planned;
                            borderColor = STATUS_BORDER_COLOR.planned;
                          } else if (
                            leaveType.toLowerCase().includes('annual leave')
                          ) {
                            bg = STATUS_COLOR['annual leave'];
                            borderColor = STATUS_BORDER_COLOR['annual leave'];
                          } else if (
                            leaveType.toLowerCase().includes('sick leave')
                          ) {
                            bg = STATUS_COLOR['sick leave'];
                            borderColor = STATUS_BORDER_COLOR['sick leave'];
                          } else {
                            bg = STATUS_COLOR['other leave'];
                            borderColor = STATUS_BORDER_COLOR['other leave'];
                          }
                        }

                        let title = '';
                        if (hits.length > 0) {
                          title = hits
                            .map(
                              l =>
                                `${l.type}: ${l.start.format('YYYY-MM-DD')} - ${l.end.format('YYYY-MM-DD')}`,
                            )
                            .join('\n');
                        }

                        return (
                          <td
                            key={wi}
                            style={{
                              cursor: hits.length ? 'help' : 'default',
                            }}
                            title={title}
                            className="right-cell"
                          >
                            {hits.length > 0 && (
                              <div
                                className="highlighted-cell"
                                style={{
                                  backgroundColor: bg,
                                  border: `1.5px solid ${borderColor}`,
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
